import React, { useEffect, useRef, useState } from 'react'
import Hls from 'hls.js'

/**
 * Plays an HLS (.m3u8) live stream. Chrome / Edge / Firefox cannot play HLS
 * in a plain <video> tag (it silently stays at 0:00), so hls.js is used
 * there; Safari plays HLS natively and gets the URL directly.
 */
const HlsPlayer = ({ src, onError, style }) => {
    const videoRef = useRef(null)
    const [failed, setFailed] = useState(false)

    useEffect(() => {
        const video = videoRef.current
        if (!video || !src) return undefined

        setFailed(false)
        let hls = null

        const fail = (reason) => {
            setFailed(true)
            if (onError) onError(reason)
        }

        if (Hls.isSupported()) {
            hls = new Hls({
                // HikCentral serves a plain live playlist with a short window
                // (a handful of segments). Low-latency mode and the default
                // three-segment sync distance keep the player at the very edge,
                // where it stalls (spinner) waiting for the next segment.
                lowLatencyMode: false,
                liveSyncDurationCount: 2,
                liveMaxLatencyDurationCount: 6,
                liveDurationInfinity: true,   // show it as LIVE, not 0:19 / 0:23
                maxBufferLength: 20,
                backBufferLength: 30,
                manifestLoadingTimeOut: 20000,
                levelLoadingTimeOut: 20000,
                fragLoadingTimeOut: 20000,
                enableWorker: true
            })
            hls.loadSource(src)
            hls.attachMedia(video)
            hls.on(Hls.Events.MANIFEST_PARSED, () => {
                video.play().catch(() => { })
            })
            hls.on(Hls.Events.ERROR, (_, data) => {
                if (!data?.fatal) return
                switch (data.type) {
                    case Hls.ErrorTypes.NETWORK_ERROR:
                        // Live playlists can briefly 404 while the DVR starts the
                        // stream; retry once, then give up.
                        if (!hls._retried) {
                            hls._retried = true
                            hls.startLoad()
                        } else {
                            fail(data)
                        }
                        break
                    case Hls.ErrorTypes.MEDIA_ERROR:
                        hls.recoverMediaError()
                        break
                    default:
                        fail(data)
                }
            })
        } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
            video.src = src
            const onNativeError = () => fail(video.error)
            video.addEventListener('error', onNativeError)
            video.play().catch(() => { })
            return () => {
                video.removeEventListener('error', onNativeError)
                video.removeAttribute('src')
                video.load()
            }
        } else {
            fail(new Error('HLS is not supported in this browser'))
        }

        return () => {
            if (hls) hls.destroy()
        }
    }, [src, onError])

    return (
        <video
            ref={videoRef}
            controls
            autoPlay
            muted
            playsInline
            data-failed={failed ? 'true' : undefined}
            style={style}
        />
    )
}

export default HlsPlayer
